import React, { useEffect, useRef } from 'react';
import { useRouteError } from 'react-router-dom';
import { colors, fonts, layout, buttons, messages } from '../styles/common';
import { ROUTES } from '../config/routes';
import { errorLogger, describeError } from '../utils/errorLogger';
import { isTranslationDomError, reloadWithTranslationDisabled } from '../utils/translationRecovery';

/**
 * ルート単位のエラー画面
 * react-router の errorElement として使用し、描画中の例外をキャッチして
 * ユーザー向けの案内を表示する（デフォルトのスタックトレース画面を出さないため）
 *
 * 404は catch-all ルート（ROUTES.NOT_FOUND）の NotFoundPage が担当するため、
 * ここでは扱わない
 */

const styles = {
  page: {
    ...layout.fullScreenCenter,
    minHeight: '70vh'
  },
  container: {
    ...layout.container,
    textAlign: 'center'
  },
  icon: {
    fontSize: '48px',
    lineHeight: '1',
    marginBottom: '15px'
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: colors.primary,
    margin: '0 0 15px 0'
  },
  message: {
    fontSize: '15px',
    color: '#333',
    lineHeight: '1.8',
    margin: '0 0 20px 0'
  },
  hintBox: {
    ...messages.info,
    borderRadius: '4px',
    padding: '12px 16px',
    fontSize: '14px',
    lineHeight: '1.8',
    textAlign: 'left',
    margin: '0 0 20px 0'
  },
  buttonArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  primaryButton: {
    ...buttons.base,
    ...buttons.primary
  },
  linkButton: {
    ...buttons.base,
    ...buttons.link
  },
  details: {
    marginTop: '20px',
    textAlign: 'left',
    fontSize: '12px',
    color: colors.textGray
  },
  pre: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    backgroundColor: colors.lightGray,
    border: `1px solid ${colors.borderGray}`,
    borderRadius: '4px',
    padding: '10px',
    fontFamily: 'monospace',
    maxHeight: '200px',
    overflowY: 'auto'
  }
};

const RouteErrorBoundary = () => {
  const error = useRouteError();
  const isTranslationError = isTranslationDomError(error);
  const headingRef = useRef(null);

  // 画面が差し替わるとフォーカスが外れた要素に残るため、見出しへ移す
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // バックエンドにエラーを記録（送信結果は待たない）
  useEffect(() => {
    const context = isTranslationDomError(error) ? '[ROUTE ERROR/TRANSLATION]' : '[ROUTE ERROR]';
    if (error instanceof Error) {
      errorLogger.logError(context, error);
    } else {
      errorLogger.log(context, describeError(error));
    }
  }, [error]);

  const handleReload = () => {
    if (isTranslationError) {
      // そのまま再読み込みすると再度翻訳されて同じ場所で落ちるため、翻訳を止めて読み込む
      reloadWithTranslationDisabled();
      return;
    }
    window.location.reload();
  };

  const handleBackToTop = () => {
    // ルーターの状態が壊れている可能性があるため、通常遷移ではなく再読み込みで戻す
    window.location.href = ROUTES.ROOT;
  };

  return (
    <div style={styles.page}>
      <div style={styles.container} role="alert">
        <div style={styles.icon} aria-hidden="true">⚠️</div>
        <h1 style={styles.title} ref={headingRef} tabIndex={-1}>一時的なエラーが発生しました</h1>
        <p style={styles.message}>
          ご迷惑をおかけしております。お手数ですが、ページを再読み込みしてお試しください。
          入力途中の内容は保存されていない場合があります。
        </p>

        {isTranslationError && (
          <div style={styles.hintBox}>
            <strong>ブラウザの翻訳機能が原因の可能性があります</strong>
            <br />
            <span>下の「翻訳をオフにして再読み込み」を押すと、翻訳を止めた状態で開き直します。</span>
          </div>
        )}

        <div style={styles.buttonArea}>
          <button type="button" style={styles.primaryButton} onClick={handleReload}>
            {isTranslationError ? '翻訳をオフにして再読み込み' : 'ページを再読み込み'}
          </button>
          <button type="button" style={styles.linkButton} onClick={handleBackToTop}>
            最初の画面に戻る
          </button>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <div style={styles.details}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px', fontFamily: fonts.main }}>
              開発用エラー詳細:
            </div>
            <pre style={styles.pre} translate="no">
              {describeError(error)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteErrorBoundary;
